import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Copy, Plus, ShieldCheck } from "lucide-react";
import { type FormEvent, useState } from "react";
import { request } from "../api";
import { useAuth } from "../auth";
import { ConfirmForm, ErrorState, LoadingState, Modal, PageHeader, Panel, StatusPill } from "../components";
import { formatTime } from "../format";
import { backofficePath } from "../paths";
import type { Role, User, UserStatus } from "../types";

interface Invitation {
  user: User;
  setup_token: string;
  expires_at: string;
}

export function UsersPage() {
  const { session } = useAuth();
  const queryClient = useQueryClient();
  const [inviting, setInviting] = useState(false);
  const [invitation, setInvitation] = useState<Invitation>();
  const [accessChange, setAccessChange] = useState<{ user: User; role: Role; status: UserStatus }>();
  const users = useQuery({ queryKey: ["users"], queryFn: () => request<User[]>("/users") });
  const update = useMutation({
    mutationFn: ({ user, role, status, reason }: { user: User; role: Role; status: UserStatus; reason: string }) => request<User>(
      `/users/${encodeURIComponent(user.id)}/access`,
      { method: "PATCH", body: JSON.stringify({ role, status, reason }) },
    ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["users"] });
      setAccessChange(undefined);
    },
  });
  const isAdmin = session?.user.role === "admin";
  const invitationSetupURL = invitation
    ? `${window.location.origin}${backofficePath(`/setup?token=${encodeURIComponent(invitation.setup_token)}`)}`
    : "";

  return (
    <>
      <PageHeader
        eyebrow="Operator access"
        title="People permitted inside the control room"
        description="Nexus accounts are independent from tenant users. Admin and operator accounts require MFA."
        actions={isAdmin ? <button type="button" className="button button--primary" onClick={() => setInviting(true)}><Plus size={16} /> Invite operator</button> : undefined}
      />
      <Panel>
        {users.isLoading && <LoadingState />}
        {users.error && <ErrorState error={users.error} />}
        <div className="operator-list">
          {users.data?.map((user) => (
            <article className="operator-row" key={user.id}>
              <div className="operator-row__avatar">{user.display_name.slice(0, 2).toUpperCase()}</div>
              <div className="operator-row__identity"><strong>{user.display_name}</strong><span>{user.email}</span></div>
              <StatusPill value={user.status} />
              <div><small>Last login</small><span>{formatTime(user.last_login_at)}</span></div>
              {isAdmin ? (
                <>
                  <select
                    aria-label={`${user.display_name} role`}
                    value={user.role}
                    disabled={update.isPending || user.id === session.user.id}
                    onChange={(event) => setAccessChange({ user, role: event.target.value as Role, status: user.status })}
                  >
                    <option value="viewer">Viewer</option><option value="operator">Operator</option><option value="admin">Admin</option>
                  </select>
                  <select
                    aria-label={`${user.display_name} status`}
                    value={user.status}
                    disabled={update.isPending || user.id === session.user.id || user.status === "invited"}
                    onChange={(event) => setAccessChange({ user, role: user.role, status: event.target.value as UserStatus })}
                  >
                    <option value="active">Active</option><option value="suspended">Suspended</option>
                    {user.status === "invited" && <option value="invited">Invited</option>}
                  </select>
                </>
              ) : <strong>{user.role}</strong>}
            </article>
          ))}
        </div>
        {update.error && <div className="inline-error">{update.error.message}</div>}
      </Panel>
      {inviting && (
        <InviteModal
          onClose={() => setInviting(false)}
          onInvited={async (value) => {
            setInvitation(value);
            setInviting(false);
            await queryClient.invalidateQueries({ queryKey: ["users"] });
          }}
        />
      )}
      {invitation && (
        <Modal title="One-time setup link" onClose={() => setInvitation(undefined)}>
          <div className="stack-form">
            <div className="inline-note"><ShieldCheck size={17} /> Send this link through an approved private channel. It expires {formatTime(invitation.expires_at)}.</div>
            <div className="copy-field"><code>{invitationSetupURL}</code><button type="button" className="icon-button" onClick={() => void navigator.clipboard.writeText(invitationSetupURL)} aria-label="Copy setup link"><Copy size={16} /></button></div>
            <button type="button" className="button button--primary" onClick={() => setInvitation(undefined)}>Done</button>
          </div>
        </Modal>
      )}
      {accessChange && (
        <Modal title={`Change access for ${accessChange.user.display_name}`} onClose={() => setAccessChange(undefined)}>
          <ConfirmForm
            action="change this operator's access"
            detail={`Role will be ${accessChange.role}; account status will be ${accessChange.status}. Active sessions are revoked when access changes.`}
            confirmLabel="Apply access change"
            busy={update.isPending}
            onCancel={() => setAccessChange(undefined)}
            onConfirm={async (reason) => {
              await update.mutateAsync({ ...accessChange, reason });
            }}
          />
          {update.error && <div className="inline-error">{update.error.message}</div>}
        </Modal>
      )}
    </>
  );
}

function InviteModal({ onClose, onInvited }: { onClose(): void; onInvited(value: Invitation): Promise<void> }) {
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<Role>("viewer");
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      await onInvited(await request<Invitation>("/users", {
        method: "POST",
        body: JSON.stringify({ email, display_name: displayName, role, reason }),
      }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Invitation could not be created.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal title="Invite Nexus operator" onClose={onClose}>
      <form className="stack-form" onSubmit={(event) => void submit(event)}>
        <label>Display name<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} /></label>
        <label>Email<input type="email" required value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>Role<select value={role} onChange={(event) => setRole(event.target.value as Role)}><option value="viewer">Viewer — read only</option><option value="operator">Operator — controls and reveals</option><option value="admin">Admin — users and settings</option></select></label>
        <label>Reason<textarea required value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Why does this person need Nexus access?" /></label>
        {error && <div className="inline-error">{error}</div>}
        <div className="form-actions"><button type="button" className="button button--ghost" onClick={onClose}>Cancel</button><button type="submit" className="button button--primary" disabled={busy}>{busy ? "Creating…" : "Create invitation"}</button></div>
      </form>
    </Modal>
  );
}
